import {Flag} from 'platform/components';
import {Box, Clickable, HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';
import {businessCaseRoutes} from '@dms/routes';
import {getBusinessCaseStateFlag} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {AuctionOffer} from '../../model/auctionView.types';
import {purchaseCaseOwner, type PurchaseCase} from './usePurchaseCase';

interface AuctionPurchaseCardsProps {
  purchaseCase: PurchaseCase | null;
  buyer: AuctionOffer | null;
  isBuyerConfirmed: boolean;
}

// Prototype v11 `.auction-case-cards`: shown only when a purchase case buys the vehicle.
export function AuctionPurchaseCards(props: AuctionPurchaseCardsProps) {
  const navigate = useNavigate();
  const stateFlag = getBusinessCaseStateFlag(props.purchaseCase?.businessCaseState);
  const buyerTitle = props.isBuyerConfirmed
    ? props.buyer?.traderName
    : i18n.t(
        props.buyer === null
          ? 'entity.auction.purchase.buyerNotSelected'
          : 'entity.auction.purchase.buyerNotConfirmed'
      );

  const purchaseCase = props.purchaseCase;
  if (purchaseCase === null) {
    return null;
  }
  const customerName = purchaseCase.customerName ?? purchaseCase.code;

  const openPurchaseCase = () =>
    navigate(
      composePath(businessCaseRoutes.overview, {
        params: {id: purchaseCase.id},
        isSearchParamsPreserved: false,
      })
    );

  return (
    <HStack spacing={3} wrap>
      <Box flexGrow={1} flexShrink={1} flexBasis={80} minWidth={0}>
        <Clickable
          role="button"
          tabIndex={0}
          aria-label={`${i18n.t('entity.auction.purchase.activePurchase')}: ${customerName}`}
          onClick={openPurchaseCase}
          onKeyDown={(event) => event.key === 'Enter' && openPurchaseCase()}
        >
          <Box
            border="1px solid"
            borderColor="palettes.neutral.40.100"
            borderRadius="small"
            backgroundColor="palettes.white.10.100"
            overflow="hidden"
            data-testid="auction-active-purchase"
          >
            <HStack>
              <Box width={1} backgroundColor="palettes.purple.60.100" />
              <Box flex={1} minWidth={0} paddingHorizontal={4} paddingVertical={3}>
                <HStack spacing={3} align="center">
                  <Box padding={2} borderRadius="small" backgroundColor="palettes.purple.20.100">
                    <Icon value="social/person" color="palettes.purple.60.100" size={6} />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <VStack>
                      <Text size="xSmall" color="secondary">
                        {i18n.t('entity.auction.purchase.activePurchase')}
                      </Text>
                      <HStack spacing={2} align="center" wrap>
                        <Text color="link" alternative data-testid="auction-active-purchase-link">
                          {customerName}
                        </Text>
                        <Show when={stateFlag}>{(flag) => <Flag {...flag} size="small" />}</Show>
                      </HStack>
                      <Text size="xSmall" color="secondary">
                        {[purchaseCase.code, purchaseCaseOwner(purchaseCase)]
                          .filter(Boolean)
                          .join(' · ')}
                      </Text>
                    </VStack>
                  </Box>
                  <Icon value="navigation/arrow_forward" color="palettes.blue.60.100" />
                </HStack>
              </Box>
            </HStack>
          </Box>
        </Clickable>
      </Box>
      <Box
        role="group"
        flexGrow={1}
        flexShrink={1}
        flexBasis={80}
        minWidth={0}
        paddingHorizontal={4}
        paddingVertical={3}
        border="1px dashed"
        borderColor="palettes.neutral.60.100"
        borderRadius="small"
        data-testid="auction-buyer-summary"
      >
        <HStack spacing={3} align="center">
          <Box padding={2} borderRadius="small" backgroundColor="palettes.neutral.30.100">
            <Icon value="communication/business" color="palettes.neutral.300.100" size={6} />
          </Box>
          <Box flex={1} minWidth={0}>
            <VStack>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.auction.purchase.buyer')}
              </Text>
              <Text color="secondary" alternative>
                {buyerTitle}
              </Text>
              <Show when={props.buyer}>
                {(buyer) => (
                  <Text size="xSmall" color="secondary" overflowWrap="anywhere">
                    {[
                      props.isBuyerConfirmed ? null : buyer.traderName,
                      buyer.traderCompany,
                      buyer.traderTier,
                    ]
                      .filter(isNotNil)
                      .join(' · ')}
                  </Text>
                )}
              </Show>
            </VStack>
          </Box>
        </HStack>
      </Box>
    </HStack>
  );
}
